import { Button, ImportantIcon, Modal, RadioGroup } from "@bp/ui-components";
import { useMemo, useState } from "react";
import { useMutation } from "urql";
import { graphql } from "../../../client/gql";
import { isFullySetUp } from "../../../utils/attention";
import { showSuccessToast, showUserErrorToast } from "../../../utils/toast";
import { InfoBox } from "../../InfoBox/InfoBox";
import { type MergeCandidate, mergePlan, suggestPrimary } from "./mergePlan";
import css from "./MergeAccountsModal.module.scss";

// Re-parents one profile and reads back where it ended up, so the caller can
// verify the move instead of trusting a silent no-op.
const MergeAccountsModal_MoveProfile = graphql(`
  mutation MergeAccountsModal_MoveProfile($where: ProfileWhere, $update: ProfileUpdateInput) {
    updateProfiles(where: $where, update: $update) {
      profiles {
        uuid
        user {
          uuid
        }
      }
    }
  }
`);

const MergeAccountsModal_DeleteUser = graphql(`
  mutation MergeAccountsModal_DeleteUser($where: UserWhere) {
    deleteUsers(where: $where) {
      __typename
    }
  }
`);

type Props = {
  email: string;
  users: MergeCandidate[];
  onClose: () => void;
  onMerged: (removedUuids: string[]) => void;
};

function candidateLabel(u: MergeCandidate): string {
  const name = u.profiles?.[0]?.listName || u.email || u.uuid;
  const parts = [
    isFullySetUp(u) ? "anmeldefähig" : "Anmeldung unvollständig",
    u.lastLogin
      ? `letzter Login ${new Date(u.lastLogin).toLocaleDateString("de-DE")}`
      : "nie angemeldet",
  ];
  if ((u.twoFactorMethods?.length ?? 0) > 0) {
    parts.push("2FA");
  }
  parts.push(`${u.profiles?.length ?? 0} Profile`);
  return `${name} — ${parts.join(" · ")}`;
}

/**
 * Resolves a duplicate-e-mail group by moving every profile onto one account and
 * deleting the emptied ones.
 *
 * Profiles are re-parented, never merged: each profile uuid survives, so anything
 * keyed on it (Matrix, Nextcloud) survives with it. What does not survive is the
 * emptied account's uuid — hence the warning and the explicit primary choice.
 * A failed move aborts before any deletion, so a half-finished run leaves both
 * accounts standing and can simply be repeated.
 */
export function MergeAccountsModal({ email, users, onClose, onMerged }: Props) {
  const [primaryUuid, setPrimaryUuid] = useState(() => suggestPrimary(users) ?? "");
  const [busy, setBusy] = useState(false);
  const [, moveProfile] = useMutation(MergeAccountsModal_MoveProfile);
  const [, deleteUser] = useMutation(MergeAccountsModal_DeleteUser);

  const plan = useMemo(() => mergePlan(users, primaryUuid), [users, primaryUuid]);
  const options = useMemo(
    () => users.map((u) => ({ label: candidateLabel(u), value: u.uuid })),
    [users],
  );

  const run = async () => {
    if (!plan || busy) {
      return;
    }
    setBusy(true);
    for (const step of plan.steps) {
      const res = await moveProfile({
        where: { uuid: step.profileUuid },
        update: {
          user: {
            disconnect: { where: { node: { uuid: step.fromUserUuid } } },
            connect: { where: { node: { uuid: plan.primary.uuid } } },
          },
        },
      });
      const landed = res.data?.updateProfiles?.profiles?.[0]?.user?.uuid;
      if (res.error || landed !== plan.primary.uuid) {
        setBusy(false);
        showUserErrorToast({
          text: `„${step.profileName}" konnte nicht umgehängt werden — abgebrochen, es wurde kein Konto gelöscht.`,
          error: res.error,
        });
        return;
      }
    }

    const removed: string[] = [];
    for (const secondary of plan.secondaries) {
      const res = await deleteUser({ where: { uuid: secondary.uuid } });
      if (res.error) {
        setBusy(false);
        showUserErrorToast({
          text: `Profile wurden umgehängt, aber ein leeres Konto blieb stehen (${secondary.uuid}).`,
          error: res.error,
        });
        onMerged(removed);
        return;
      }
      removed.push(secondary.uuid);
    }

    setBusy(false);
    showSuccessToast(
      `${email}: ${plan.steps.length} Profil(e) übernommen, ${removed.length} Konto/Konten gelöscht`,
    );
    onMerged(removed);
  };

  return (
    <Modal
      isOpen
      ariaHideApp={false}
      width="s"
      title={`Konten zusammenführen — ${email}`}
      onRequestClose={onClose}
    >
      <div className={css.stack}>
        <InfoBox tone="error" icon={<ImportantIcon style={{ fontSize: 15 }} />}>
          <span style={{ fontSize: 12 }}>
            Die Profile wechseln nur den Besitzer und bleiben vollständig erhalten. Die nicht
            gewählten <strong>Konten</strong> werden gelöscht — alles, was an der Konto-Kennung
            hängt statt am Profil (BP-Benachrichtigungen, Matrix-Verlauf), ist danach nicht mehr
            erreichbar. Wähle deshalb das Konto, das tatsächlich benutzt wird.
          </span>
        </InfoBox>

        <div>
          <span className={css.eyebrow}>Dieses Konto behalten</span>
          <RadioGroup
            name="merge-primary"
            options={options}
            value={primaryUuid}
            onChange={(value) => setPrimaryUuid(value ?? "")}
          />
        </div>

        {plan && (
          <div className={css.plan}>
            <div className={css.planLine}>
              {plan.steps.length === 0
                ? "Keine Profile umzuhängen."
                : `${plan.steps.length} Profil(e) werden übernommen:`}
            </div>
            {plan.steps.length > 0 && (
              <ul className={css.moves}>
                {plan.steps.map((s) => (
                  <li key={s.profileUuid}>{s.profileName}</li>
                ))}
              </ul>
            )}
            <div className={css.planLine}>
              Danach gelöscht: {plan.secondaries.length} Konto/Konten.
            </div>
          </div>
        )}

        <div className={css.foot}>
          <Button hierarchy="tertiary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            hierarchy="primary"
            disabled={busy || !plan || !primaryUuid}
            onClick={() => void run()}
          >
            {busy ? "Führt zusammen …" : "Zusammenführen"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
